import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Flex,
  Form,
  Input,
  Skeleton,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ChevronLeft, Pencil } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import {
  type TeamList,
  organizationsAccessListOptions,
  organizationsTeamsCreateMutation,
  organizationsTeamsListOptions,
  organizationsTeamsListQueryKey,
  organizationsTeamsPartialUpdateMutation,
  organizationsTeamsRetrieveOptions,
  organizationsTeamsRetrieveQueryKey
} from 'src/api/main'
import {
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { useDocumentTitle } from 'src/hooks'
import { paths } from 'src/router/paths'
import { useShowApiErrors } from 'src/utils'

import { orgEntityListCardHeaderStyles } from '../components/workspaceOrgChart/OrgEntityListCard'
import {
  EntityFormFields,
  type EntityFormValues,
  type TeamWithHead
} from '../components/workspaceOrgChart/WorkspaceOrgEntityPanels'

import { useWorkspaceRoute } from './WorkspacePageShell'

export const WorkspaceOrgDivisionDetailPage = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const queryClient = useQueryClient()
  const { id: workspaceRouteId = '', divisionId = '' } = useParams<{
    id: string
    divisionId: string
  }>()
  const navigate = useNavigate()
  const { workspaceId, idValid } = useWorkspaceRoute()
  const numericId = Number(divisionId)

  const [editOpen, setEditOpen] = useState(false)
  const [editForm] = Form.useForm<EntityFormValues>()
  const [addDeptOpen, setAddDeptOpen] = useState(false)
  const [deptForm] = Form.useForm<{ name: string }>()

  const {
    data: rawDivision,
    isLoading,
    isError,
    refetch
  } = useQuery({
    ...organizationsTeamsRetrieveOptions({ path: { id: numericId } }),
    enabled: !Number.isNaN(numericId) && numericId > 0
  })

  const division = rawDivision as TeamWithHead | undefined

  useDocumentTitle(division?.name)

  // Child departments (type=department, parent=this division)
  const { data: childDepsPage } = useQuery({
    ...organizationsTeamsListOptions({
      query: {
        organization: workspaceId,
        type: ['department'],
        per_page: 200
      }
    }),
    enabled: idValid && !Number.isNaN(numericId) && numericId > 0
  })

  const childDepartments = useMemo(
    () => (childDepsPage?.results ?? []).filter((d) => d.parent === numericId),
    [childDepsPage, numericId]
  )

  // Org members for owner select
  const { data: accessPage } = useQuery({
    ...organizationsAccessListOptions({
      query: { organization: workspaceId, per_page: 200 }
    }),
    enabled: idValid
  })

  const memberOptions = useMemo(
    () =>
      (accessPage?.results ?? []).map((a) => ({
        label:
          `${a.user.first_name} ${a.user.last_name}`.trim() || a.user.email,
        value: a.user.id
      })),
    [accessPage]
  )

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: organizationsTeamsRetrieveQueryKey({
          path: { id: numericId }
        })
      }),
      queryClient.invalidateQueries({
        queryKey: organizationsTeamsListQueryKey()
      })
    ])
  }

  const updateMutation = useMutation({
    ...organizationsTeamsPartialUpdateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.divisionUpdated'))
      setEditOpen(false)
      await invalidateAll()
    },
    onError: (e) => showApiErrors(e)
  })

  const createDeptMutation = useMutation({
    ...organizationsTeamsCreateMutation(),
    onSuccess: async () => {
      message.success(t('workspace.orgChart.messages.departmentAdded'))
      setAddDeptOpen(false)
      deptForm.resetFields()
      await invalidateAll()
    },
    onError: (e) => showApiErrors(e)
  })

  const startEditing = () => {
    if (!division) return
    editForm.setFieldsValue({
      name: division.name,
      abbreviation: division.abbreviation ?? undefined,
      description: division.description ?? undefined,
      head_user: division.head_user ?? undefined
    })
    setEditOpen(true)
  }

  const ownerLabel = useMemo(() => {
    if (division?.head_user_detail) {
      const d = division.head_user_detail

      return `${d.first_name} ${d.last_name}`.trim() || d.email
    }
    if (division?.top_users?.length) {
      return division.top_users
        .map((u) => `${u.first_name} ${u.last_name}`.trim() || u.email)
        .join(', ')
    }
    return '—'
  }, [division])

  const columns: ColumnsType<TeamList> = useMemo(
    () => [
      {
        fixed: 'left',
        title: t('workspace.orgChart.columns.name'),
        dataIndex: 'name'
      },
      {
        title: t('workspace.orgChart.columns.members'),
        dataIndex: 'users_count'
      },
      {
        title: <TableActionColumnTitle />,
        key: 'actions',
        width: 120,
        render: (_, row) => (
          <Button
            type="link"
            onClick={() =>
              navigate(
                paths.workspaces.membersDepartment(
                  workspaceRouteId,
                  String(row.id)
                )
              )
            }
          >
            {t('workspace.orgChart.actions.manage')}
          </Button>
        )
      }
    ],
    [t, navigate, workspaceRouteId]
  )

  const backButton = (
    <Button
      type="link"
      icon={<ChevronLeft size={16} />}
      onClick={() => navigate(paths.workspaces.members(workspaceRouteId))}
      style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
    >
      {t('workspace.orgChart.backToMembers')}
    </Button>
  )

  if (isLoading) {
    return (
      <Flex vertical gap={16}>
        {backButton}
        <Card size="small" variant="outlined">
          <Skeleton active />
        </Card>
      </Flex>
    )
  }

  if (isError) {
    return (
      <Flex vertical gap={16}>
        {backButton}
        <QueryErrorAlert onRetry={() => void refetch()} />
      </Flex>
    )
  }

  if (!division) {
    return (
      <Flex vertical gap={16}>
        {backButton}
        <Typography.Title level={5}>
          {t('workspace.orgChart.notFound')}
        </Typography.Title>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={16}>
      {backButton}

      <Card
        size="small"
        variant="outlined"
        title={division.name}
        styles={orgEntityListCardHeaderStyles}
        extra={
          <Button
            type="text"
            icon={<Pencil size={14} />}
            onClick={startEditing}
          >
            {t('workspace.orgChart.actions.edit')}
          </Button>
        }
      >
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item
            label={t('workspace.orgChart.fields.abbreviation')}
          >
            {division.abbreviation || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.fields.description')}>
            {division.description || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.fields.owner')}>
            {ownerLabel}
          </Descriptions.Item>
          <Descriptions.Item label={t('workspace.orgChart.columns.members')}>
            {division.users_count}
          </Descriptions.Item>
          <Descriptions.Item
            label={t('workspace.orgChart.columns.departments')}
          >
            {childDepartments.length}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <ProTableContainer<TeamList>
        showSearch={false}
        rowKey="id"
        columns={columns}
        dataSource={childDepartments}
        pagination={false}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
        leftChildren={
          <Flex vertical gap={2}>
            <Typography.Text strong>
              {t('workspace.orgChart.division.departmentsTableTitle')}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.orgChart.division.departmentsTableHint')}
            </Typography.Text>
          </Flex>
        }
        rightChildren={
          <Button
            size="small"
            type="primary"
            onClick={() => {
              deptForm.resetFields()
              setAddDeptOpen(true)
            }}
          >
            {t('workspace.orgChart.departments.add')}
          </Button>
        }
      />

      {/* Edit drawer */}
      <Drawer
        title={t('workspace.orgChart.divisions.editDrawerTitle')}
        size={480}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setEditOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={updateMutation.isPending}
              onClick={() => editForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(values) => {
            updateMutation.mutate({
              path: { id: numericId },
              body: {
                name: values.name,
                abbreviation: values.abbreviation ?? null,
                description: values.description,
                head_user: values.head_user ?? null
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
              } as any
            })
          }}
        >
          <EntityFormFields t={t} memberOptions={memberOptions} />
        </Form>
      </Drawer>

      {/* Add department drawer */}
      <Drawer
        title={t('workspace.orgChart.division.addDepartmentDrawerTitle')}
        size={480}
        open={addDeptOpen}
        onClose={() => setAddDeptOpen(false)}
        destroyOnHidden
        footer={
          <Flex justify="flex-end" gap={8}>
            <Button onClick={() => setAddDeptOpen(false)}>{t('cancel')}</Button>
            <Button
              type="primary"
              loading={createDeptMutation.isPending}
              onClick={() => deptForm.submit()}
            >
              {t('save')}
            </Button>
          </Flex>
        }
      >
        <Form
          form={deptForm}
          layout="vertical"
          onFinish={(values) => {
            createDeptMutation.mutate({
              body: {
                name: values.name,
                type: 'department',
                parent: numericId,
                organization: workspaceId
              }
            })
          }}
        >
          <Form.Item
            name="name"
            label={t('workspace.orgChart.fields.name')}
            rules={[{ required: true, message: t('required_field_error') }]}
          >
            <Input placeholder={t('placeholders.name')} />
          </Form.Item>
        </Form>
      </Drawer>
    </Flex>
  )
}
